import {useState, useEffect} from 'react';
import {SearchField} from '@forecast-it/design-system';
import {useLocation} from 'react-router-dom';
import {useSearchParams, useNavigate} from '../../../router/compat';
import {useDebounce} from '../hooks/useDebounce';
import {forecastFetchQuery} from '../../../graphql/forecastRelay';
import {TicketLookupBySlugQuery, TicketLookupByIdQuery} from '../hooks/useTicketDetail';

// Mirrors TicketDetailRoute's slug split: `ACME-1042` is a slug, anything else
// (e.g. a raw ticket id) is looked up by id.
const SLUG_PATTERN = /^[A-Z][A-Z0-9]{1,9}-\d+$/;

export function GlobalSearch() {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();

	const [inputValue, setInputValue] = useState(() => searchParams.get('search') ?? '');
	const debouncedValue = useDebounce(inputValue, 200);

	// Sync input from URL when navigating back/forward
	useEffect(() => {
		const urlSearch = searchParams.get('search') ?? '';
		setInputValue(urlSearch);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key]);

	useEffect(() => {
		if (debouncedValue === '') {
			if (location.pathname === '/tickets') {
				setSearchParams(
					prev => {
						const updated = new URLSearchParams(prev);
						updated.delete('search');
						return updated;
					},
					{replace: true}
				);
			}
			return;
		}

		// #<value> prefix — attempt a direct ticket lookup (slug or id).
		if (debouncedValue.startsWith('#')) {
			const value = debouncedValue.slice(1);
			void (async () => {
				try {
					const isSlug = SLUG_PATTERN.test(value);
					const data = (await forecastFetchQuery(
						isSlug ? TicketLookupBySlugQuery : TicketLookupByIdQuery,
						isSlug ? {slug: value} : {id: value}
					)) as {
						viewer?: {
							ticketDetail?: {ticket?: unknown} | null;
							ticketDetailBySlug?: {ticket?: unknown} | null;
						} | null;
					} | null;
					const found = data?.viewer?.ticketDetail?.ticket ?? data?.viewer?.ticketDetailBySlug?.ticket ?? null;
					if (found) {
						navigate(`/tickets/${value}`);
						return;
					}
				} catch {
					// fall through to search
				}
				applySearch(debouncedValue);
			})();
			return;
		}

		applySearch(debouncedValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	function applySearch(value: string) {
		if (location.pathname === '/tickets') {
			setSearchParams(
				prev => {
					const updated = new URLSearchParams(prev);
					updated.set('search', value);
					return updated;
				},
				{replace: true}
			);
		} else {
			navigate(`/tickets?search=${encodeURIComponent(value)}`);
		}
	}

	// The design-system field is expandable: with no query it is a single search
	// button, and it grows into an input on press or focus. That keeps the header
	// free for the view switcher and the primary action.
	return (
		<SearchField
			placeholder="Search tickets…"
			value={inputValue}
			onChange={setInputValue}
			width="250px"
			data-cy="ticket-search"
		/>
	);
}
